import { ModuleTypeEnum } from '@scrambl/shared';
import { getOrgPath } from '@/utils/org-paths';

/**
 * Mapping of field names to their target routes
 */
const FIELD_NAVIGATION_MAP: Record<string, string> = {
  // Client relationships
  'entityId': '/entities',
  'client_id': '/entities',

  // Project relationships
  'projectId': '/projects',
  'project_id': '/projects',

  // Task relationships
  'taskId': '/tasks',
  'task_id': '/tasks',
  'parentTaskId': '/tasks',
  'parent_task_id': '/tasks',

  // Contact relationships
  'contactId': '/contacts',
  'contact_id': '/contacts',
  'primaryContactId': '/contacts',
  'primary_contact_id': '/contacts',

  // Deal relationships
  'dealId': '/deals',
  'deal_id': '/deals',

  // Contract relationships
  'contractId': '/contracts',
  'contract_id': '/contracts',

  // Invoice relationships
  'invoiceId': '/invoices',
  'invoice_id': '/invoices',

  // Event relationships
  'eventId': '/events',
  'event_id': '/events',

  // Document relationships
  'documentId': '/documents',
  'document_id': '/documents',

  // Supplier relationships
  'supplierId': '/suppliers',
  'supplier_id': '/suppliers',
};

/**
 * Mapping of module types to their routes
 */
const MODULE_TYPE_ROUTES: Partial<Record<ModuleTypeEnum, string>> = {
  [ModuleTypeEnum.ENTITY]: '/entities',
  [ModuleTypeEnum.PROJECT]: '/projects',
  [ModuleTypeEnum.TASK]: '/tasks',
  [ModuleTypeEnum.TICKET]: '/tickets',
  [ModuleTypeEnum.DEAL]: '/deals',
  [ModuleTypeEnum.LEAD]: '/contacts',
  [ModuleTypeEnum.CONTACT]: '/contacts',
  [ModuleTypeEnum.EVENT]: '/events',
  [ModuleTypeEnum.CONTRACT]: '/contracts',
  [ModuleTypeEnum.INVOICE]: '/invoices',
  [ModuleTypeEnum.SUPPLIER]: '/suppliers',
  [ModuleTypeEnum.THREAD]: '/threads',
  [ModuleTypeEnum.TIME_ENTRY]: '/time',
  [ModuleTypeEnum.NOTE]: '/notes',
  [ModuleTypeEnum.DOCUMENT]: '/documents',
  [ModuleTypeEnum.CUSTOM_COLLECTION]: '/collections',
  [ModuleTypeEnum.FORM]: '/forms',
  [ModuleTypeEnum.ORGANIZATION]: '/my-organization',
};

/**
 * Determines if a field is navigable (represents a relationship to another entity)
 */
export function isNavigableField(fieldName: string, entity: Record<string, unknown>): boolean {
  // Check if field name is in our navigation map
  if (FIELD_NAVIGATION_MAP[fieldName]) {
    return true;
  }
  
  // Check if field ends with 'Id' and has a corresponding related entity or value
  if (fieldName.endsWith('Id') || fieldName.endsWith('_id')) {
    const relationKey = fieldName.replace(/Id$/, '').replace(/_id$/, '');
    const hasRelatedEntity = !!entity[relationKey];
    const hasFieldValue = !!(entity[fieldName]);
    
    return hasRelatedEntity || hasFieldValue;
  }
  
  return false;
}

/**
 * Gets the navigation path for a relationship field
 */
export function getFieldNavigationPath(
  fieldName: string, 
  fieldValue: string | number,
  orgSlug: string
): string | null {
  if (!fieldValue || !orgSlug) {
    return null;
  }
  
  // Direct mapping from field name
  const basePath = FIELD_NAVIGATION_MAP[fieldName];
  if (basePath) {
    const fullPath = `${basePath}/${fieldValue}`;
    return getOrgPath(orgSlug, fullPath);
  }
  
  // Try to infer from field name pattern
  if (fieldName.endsWith('Id') || fieldName.endsWith('_id')) {
    const relationKey = fieldName.replace(/Id$/, '').replace(/_id$/, '');
    
    // Special handling for common patterns
    const routeMap: Record<string, string> = {
      'client': '/entities',
      'entity': '/entities',
      'project': '/projects',
      'task': '/tasks',
      'contact': '/contacts',
      'lead': '/contacts',
      'deal': '/deals',
      'contract': '/contracts',
      'invoice': '/invoices',
      'event': '/events',
      'document': '/documents',
      'supplier': '/suppliers',
      'parentTask': '/tasks',
    };
    
    const route = routeMap[relationKey];
    if (route) {
      const fullPath = `${route}/${fieldValue}`;
      return getOrgPath(orgSlug, fullPath);
    }
  }
  
  return null;
}

/**
 * Gets the navigation path for a module type and ID
 */
export function getModuleNavigationPath(
  moduleType: ModuleTypeEnum,
  entityId: string | number,
  orgSlug: string
): string | null {
  if (!orgSlug || (moduleType !== ModuleTypeEnum.ORGANIZATION && !entityId)) {
    return null;
  }
  
  const basePath = MODULE_TYPE_ROUTES[moduleType];
  if (basePath) {
    if (moduleType === ModuleTypeEnum.ORGANIZATION) {
      return getOrgPath(orgSlug, basePath);
    }
    const fullPath = `${basePath}/${entityId}`;
    return getOrgPath(orgSlug, fullPath);
  }
  
  return null;
}

/**
 * Gets the relationship entity ID from a field value or related entity
 */
export function getRelationshipEntityId(
  fieldName: string,
  fieldValue: unknown,
  entity: Record<string, unknown>
): string | number | null {
  // FIRST PRIORITY: Try to get ID from the entity field directly (e.g., entity.entityId)
  // This is the actual ID field in the database
  const directValue = entity[fieldName];
  if (typeof directValue === 'string' || typeof directValue === 'number') {
    return directValue;
  }
  
  // SECOND PRIORITY: Try to get ID from related entity (e.g., entity.client.id)
  if (fieldName.endsWith('Id') || fieldName.endsWith('_id')) {
    const relationKey = fieldName.replace(/Id$/, '').replace(/_id$/, '');
    const relatedEntity = entity[relationKey] as Record<string, unknown> | undefined;
    
    if (relatedEntity && typeof relatedEntity === 'object') {
      const entityId = relatedEntity.id || relatedEntity.uuid;
      if (entityId) {
        return entityId as string | number;
      }
    }
  }
  
  // LAST RESORT: If field value looks like a UUID or number ID, use it
  // (But display values like "Yolk Labs (Internal)" should not match this)
  if (typeof fieldValue === 'string') {
    // Check if it's a UUID pattern
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (uuidRegex.test(fieldValue)) {
      return fieldValue;
    }
  }
  
  if (typeof fieldValue === 'number') {
    return fieldValue;
  }
  
  return null;
}
